import { HTMLAttributes, useState } from 'react';
import maiaLogo from './../../assets/maia-logo.svg';
import maiaLogoSmall from './../../assets/maia-logo-small.svg';
import { twMerge } from 'tailwind-merge';
import { Link } from 'react-aria-components';

import {
  IconApps,
  IconCheckSquare,
  IconFire,
  IconReceipt,
  IconShieldCheck,
  IconUsersAlt,
} from '@maia/icons';

import { SidenavLink } from './sidenavLink';
import { createContext } from 'react';
import { Divider } from '../../atoms/divider/divider';
import { linkOptions } from '@tanstack/react-router';
import { ButtonLink, Typography } from '../../ui';

type Props = {} & HTMLAttributes<HTMLDivElement>;

export const SidenavContext = createContext({ isFullWidth: true });

export function Sidenav(props: Props) {
  const { className, ...rest } = props;
  const [isFullWidth] = useState<boolean>(true);

  return (
    <SidenavContext.Provider value={{ isFullWidth: isFullWidth }}>
      <aside
        className={twMerge(
          'border-r h-full border-r-grayscale100 flex flex-col',
          className,
        )}
        aria-label="Navigeer de Broker Studio"
        {...rest}
      >
        {/* Logo container */}
        <nav className="px-32 py-40">
          <Link href="/">
            {isFullWidth ? (
              <img
                src={maiaLogo}
                alt="Maia logo"
                className="w-64 h-20 object-contain"
              />
            ) : (
              <img
                src={maiaLogoSmall}
                alt="Maia logo"
                className="w-64 h-20 object-contain"
              />
            )}
          </Link>
        </nav>

        {/* Main nav */}
        <nav className="px-20 py-12">
          <ul className="space-y-20">
            <li>
              <SidenavLink
                {...linkOptions({
                  to: '/dashboard',
                  label: 'Dashboard',
                  activeProps: {
                    className: 'bg-primary50',
                  },
                })}
                icon={IconApps}
              />
            </li>
            <li>
              <SidenavLink
                {...linkOptions({
                  to: '/customers',
                  label: 'Klanten',
                  activeProps: {
                    className: 'bg-primary50',
                  },
                })}
                icon={IconUsersAlt}
              />
            </li>
            <li>
              <SidenavLink
                {...linkOptions({
                  to: '/insurances',
                  label: 'Verzekeringen',
                  activeProps: {
                    className: 'bg-primary50',
                  },
                })}
                icon={IconShieldCheck}
              />
            </li>
            <li>
              <SidenavLink
                {...linkOptions({
                  to: '/claims',
                  label: 'Claims',
                  activeProps: {
                    className: 'bg-primary50',
                  },
                })}
                icon={IconFire}
              />
            </li>
            <li>
              <SidenavLink
                {...linkOptions({
                  to: '/tasks',
                  label: 'Taken',
                  activeProps: {
                    className: 'bg-primary50',
                  },
                })}
                icon={IconCheckSquare}
              />
            </li>
            <li>
              <SidenavLink
                {...linkOptions({
                  to: '/quotations',
                  label: 'Offertes',
                  activeProps: {
                    className: 'bg-primary50',
                  },
                })}
                icon={IconReceipt}
              />
            </li>
          </ul>
        </nav>

        <div className="px-20">
          <Divider className="my-16" />
        </div>

        <nav className="px-20 py-12 pt-32">
          <div className="bg-primary50 relative p-12 rounded-12 pt-24 max-w-[164px]">
            <img
              src={`/images/illustrations/feedback.svg`}
              alt={'Geen resultaten gevonden'}
              className="object-contain w-[88px] h-[66px] absolute -left-14 -top-1/3"
            />
            <Typography
              variant="body-m"
              weight="semibold"
              className="text-primaryMain block"
            >
              Iets gespot wat beter kan?
            </Typography>
            <ButtonLink
              size="small"
              to="https://maia-insure.atlassian.net/servicedesk/customer/portals"
              target="_blank"
              rel="noreferrer"
              className="block text-center mt-8"
            >
              Deel je ervaring
            </ButtonLink>
          </div>
        </nav>
      </aside>
    </SidenavContext.Provider>
  );
}
